import { useDebounceFn } from '@vueuse/core'
import moment from 'moment'
import { computed, nextTick, ref, toRef, watch } from 'vue'
import { onBeforeRouteLeave, onBeforeRouteUpdate } from 'vue-router'

import { del, get, getBlobJson, post } from '@/api'
import { useFilePond } from '@/composables/useFilePond'
import { allowedEmbedFileTypes } from '@/utils'
import {
  matchFileExtensionToIconClasses,
  modelNameSingularize,
  prettySize,
} from '@/utils'
import { downloadFile, resolveModelMetaFromId } from '@/utils/api'

const TIME_TIMESTAMP_GEN = 5 // 5 minutes to generate a timestamp is fair

const prettyDate = (date) => moment(date).format("D MMM 'YY, HH:mm")

const uuid = ref(null)
const appName = ref(null)
const modelName = ref(null)
const singularModelName = computed(
  () => modelName.value && modelNameSingularize(modelName.value),
)
const rawAttachments = ref([])
const isLoading = ref(false)
const imagesReadyForEmbed = ref([])

export function useAttachments(
  id,
  modelMeta = { appName: null, modelName: null },
) {
  // Capability gate: when disabled (e.g. a non-attachmentable model), the
  // composable is still constructed but never sets uuid / fetches. Reactive so
  // it lights up if metadata flips attachmentable after load.
  const isEnabled = toRef(modelMeta.enabled ?? true)
  watch(
    () => uuid.value,
    (_uuid) => {
      if (_uuid && (!modelMeta.appName || !modelMeta.modelName)) {
        resolveModelMetaFromId(_uuid).then((meta) => {
          appName.value = meta.appName
          modelName.value = meta.modelName
        })
      } else {
        appName.value = modelMeta.appName
        modelName.value = modelMeta.modelName
      }
    },
  )
  const baseUrl = computed(
    () =>
      (uuid.value &&
        appName.value &&
        `attachments/` +
          `?app=${appName.value}` +
          `&model=${singularModelName.value}` +
          `&object_id=${uuid.value}`) ||
      null,
  )
  const fetchUrl = computed(() => {
    return (baseUrl.value && `${baseUrl.value}&page_size=max`) || null
  })

  watch(
    () => fetchUrl.value,
    () => {
      if (isEnabled.value) loadAttachments()
    },
  )

  async function fetchAttachments() {
    let rawAttachments = null
    let response = null
    isLoading.value = true
    if (fetchUrl.value) response = await get(fetchUrl.value)
    rawAttachments = (response?.status === 200 && response?.data) || []
    isLoading.value = false
    return rawAttachments
  }

  const loadAttachments = useDebounceFn(async () => {
    rawAttachments.value = await fetchAttachments()
  }, 1000)

  const attachmentNameById = computed(() =>
    rawAttachments.value.reduce(
      (namesById, a) => ({ ...namesById, [a.id]: a.name }),
      {},
    ),
  )

  const attachments = computed(() => {
    return rawAttachments.value.map((a) => {
      let iconClasses = matchFileExtensionToIconClasses(a.filetype)
      iconClasses = iconClasses
        ? [...iconClasses, 'fa-2x']
        : ['fa-regular', 'fa-file', 'fa-2x']
      return {
        created: a.created,
        modified: a.modified,
        prettyDates: {
          created: prettyDate(a.created),
          modified: prettyDate(a.modified),
        },
        createdBy: a.created_by,
        editUrl: a.edit_url,
        directUrl: a.direct_url,
        id: a.id,
        isAutogenerated: a.autogenerated === true,
        isEmbedded: a.embedded === true,
        isEmbeddable: allowedEmbedFileTypes.includes(a.mimetype),
        isExport: a.is_export === true,
        modifiedBy: a.modified_by,
        name: a.name,
        fileType: a.filetype,
        mimeType: a.mimetype,
        preview: a.peek,
        size: prettySize(a.size),
        thumbnailUrl: a.thumbnail_url,
        tsrUrl: a.tsr_url,
        wopiTokenUrl: a.wopi_token_url,
        iconClasses,
        isWithinTimeStampGenerationDelta:
          moment().diff(moment(a.modified), 'minutes') < TIME_TIMESTAMP_GEN,
        hasBeenModifiedAfterCreation:
          moment(a.modified).diff(moment(a.created), 'second') > 0,
      }
    })
  })

  const { getUploadFileFormData } = useFilePond()
  async function uploadAttachment(fileData, fileName, embedded) {
    const formData = getUploadFileFormData('file', fileData, fileName)
    if (embedded === true) formData.append('embedded', 'True')
    return post(baseUrl.value, { data: formData })
  }

  const deleteSuccesses = ref([])
  const deletingAttachmentsIds = ref([])

  async function deleteAttachment(attachmentId) {
    deletingAttachmentsIds.value.push(attachmentId)
    await nextTick()
    let response
    try {
      response = await del(`attachments/${attachmentId}/`)
    } catch (e) {
      console.error(`Error [deleteAttachment: ${attachmentId}]: `, e)
    }
    if (response.status === 204) {
      const attachmentName = attachmentNameById.value[attachmentId]
      deleteSuccesses.value.push(attachmentName)
      setTimeout(() => {
        const iDel = deleteSuccesses.value.indexOf(attachmentName)
        iDel !== -1 && deleteSuccesses.value.splice(iDel, 1)
      }, 5000)
    }
    setTimeout(() => {
      const iDel = deletingAttachmentsIds.value.indexOf(attachmentId)
      iDel !== -1 && deletingAttachmentsIds.value.splice(iDel, 1)
    }, 5000)
    return response
  }

  function deleteAttachments(attachmentIds) {
    return Promise.all(attachmentIds.map((id) => deleteAttachment(id)))
  }

  function doAndReload(doFunc) {
    return async (...args) => {
      const response = doFunc && (await doFunc(...args))
      loadAttachments()
      return response
    }
  }
  function downloadAttachment(attachmentId, name) {
    const url = `attachments/${attachmentId}/download/`
    return downloadFile({ url, name })
  }
  function getAttachmentBlob(attachmentId) {
    return getBlobJson(`attachments/${attachmentId}/download/`, {
      responseType: 'blob',
    })
  }

  async function uploadImageCopy(image, imageName) {
    const imageId = image.id
    const resonseBlob = await getAttachmentBlob(imageId)
    const imageData = resonseBlob?.data
    return uploadAttachment(imageData, imageName, true)
  }

  const isUploading = ref(false)
  async function handleAttachmentsUpload(attachmentsData) {
    isUploading.value = true
    const uploadActions = attachmentsData.map(
      ({ fileData, fileName, embedded }) =>
        uploadAttachment(fileData, fileName, embedded),
    )
    const uploadedAttachments = await Promise.all(uploadActions)
    isUploading.value = false
    return uploadedAttachments
  }

  function unloadAttachments() {
    uuid.value = null
    appName.value = null
    modelName.value = null
    rawAttachments.value = []
  }

  if (id) {
    // Only claim the singleton / trigger a fetch once enabled. Reactive so a
    // late metadata flip to attachmentable still wires up correctly.
    watch(
      isEnabled,
      (on) => {
        if (on) uuid.value = id
      },
      { immediate: true },
    )

    onBeforeRouteUpdate(async (to, from) => {
      if (from.params.id !== to.params.id) unloadAttachments()
    })

    onBeforeRouteLeave(() => {
      unloadAttachments()
    })
  }

  return {
    getAttachmentUrl: baseUrl,
    modelName,
    singularModelName,
    rawAttachments,
    attachments,
    loadAttachments,
    unloadAttachments,
    isLoading,
    uploadAttachment,
    handleAttachmentsUpload,
    isUploading,
    deleteAttachment,
    deleteAttachments: doAndReload(deleteAttachments),
    deletingAttachmentsIds,
    downloadAttachment,
    getAttachmentBlob,
    imagesReadyForEmbed,
    uploadImageCopy,
    deleteSuccesses,
    doAndReload,
  }
}
